import { Box, Card, TextField as MuiTextField, Tooltip, IconButton, Typography, Divider, CardHeader } from "@mui/material";
import { ChangeEvent, FC, useCallback, useContext, useState } from "react";
import { RecPatch } from "~/dataProvider/tools";
import { NetworkConnectionResponseDto, NetworkConnectionStatus, NetworkGatewayStatus } from "~/gen/api/deploy";
import { genUrlWithHashid } from "~/utils/hashid";
import { FunctionField, TextField } from "../field";
import DateField2 from "../field/DateField2";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import Confirm from "../widgets/Confirm";
import ArrowRightIcon from '~/icons/ArrowRight';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import { Link as RouterLink } from 'react-router-dom'
import { useListContext } from "ra-core";
import { useNetworkConfigurationContext } from "~/pages/deployment/useNetworkConfigurationContext";
import DefaultPagination from '~/components/list/pagination/Pagination'
import NewNetworkConnection from "./NewConnection";
import { StatusIndicator } from "./DeploymentInfo";
import { CLOUD_MANAGEMENT } from "~/constants";
import { NetworkConfigureContext } from "./NetworkGatewayInfo";
import { useHasPermit } from "~/authProvider/useHasPermit";


const NetworkConnectTable: FC = () => {
  const hasWritePermit = useHasPermit([CLOUD_MANAGEMENT])
  const { shouldSuspendUsage } = useContext(NetworkConfigureContext)
  
  const listCtx = useListContext()
  
  const [objToDel, setObjToDel] = useState<NetworkConnectionResponseDto | null>(null)
  const [nameConfirm, setNameConfirm] = useState('')

  const nameConfirmed = !!(objToDel && (objToDel.name?.toLowerCase() === nameConfirm?.toLowerCase()))

  const { deleteNetworkConn, deleting, vpnGatewayRec } = useNetworkConfigurationContext()

  const handleDelete = 
    objToDel 
    ? () => { 
      deleteNetworkConn(objToDel.networkConnectionId || -1, {
        onSuccess: () => handleDeleteDialogClose()
      }) 
    }
    : () => {};

  const handleNameInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNameConfirm(e.target.value)
  }, [])

  const handleDeleteDialogClose = () => {
    setObjToDel(null)
    setNameConfirm('')
  }

  const [openCreate, setOpenCreate] = useState(false)

  return (
    <Box>
      <Card sx={{ mt: 3 }}>
        <CardHeader 
          title="Network Connections" 
          action={
            vpnGatewayRec && !shouldSuspendUsage && hasWritePermit &&
              <Tooltip title="Add">
                <IconButton 
                  disabled={vpnGatewayRec.status !== NetworkGatewayStatus.Success}
                  sx={{ 
                    color: 'text.primary' as any, 
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => setOpenCreate(true)}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
          }
        />
        <Divider />
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid
              hasBulkActions={false}
              sx={{
                '& th': { py: 2 },
                '& td': { py: 2 },
              }}
              {...listCtx}
            >
              <TextField label="Name" source="name" sortable={true} />
              <TextField label="Type" source="typeText" sortable={false} />
              {/* <TextField label="Performance Tier" source="performanceTier" sortable={false} /> */}
              <TextField label="Destination IP" source="localPublicIp" sortable={false} />
              <DateField2 label="Creation Time" source="createdTime" showTime sortable={false} />
              <FunctionField 
                label="Status" 
                source="status" 
                sortable={false} 
                render={(d?: NetworkConnectionResponseDto & RecPatch) => (
                  <StatusIndicator 
                    color={(
                        d?.status === NetworkConnectionStatus.Success ? 'success.main'
                      : d?.status === NetworkConnectionStatus.Failed ? 'error.main'
                      : 'text.disabled') as any
                    }
                    title={
                      !d ? undefined
                      : (NetworkConnectionStatus[d.status as any] || '').replace(/[A-Z]+/, (m:string) => ' ' + m)
                    }
                  />

                )}
              />
              <FunctionField
                label="Action"
                sortable={false}
                render={(d?: NetworkConnectionResponseDto & RecPatch) => (
                  <>
                    {hasWritePermit &&
                      <Tooltip title="Delete">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={() => setObjToDel(d as any)}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>}
                    {!shouldSuspendUsage &&
                      <Tooltip title="Details">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          component={RouterLink}
                          to={genUrlWithHashid(`/settings/deployment/network-configuration/$0/show`, d?.id || 0, d?.name)}
                        >
                          <ArrowRightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>}
                  </>
                )}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
          <DefaultPagination {...listCtx} />}
      </Card>
      {hasWritePermit &&
        <Confirm
          isOpen={!!objToDel}
          loading={deleting}
          valid={nameConfirmed}
          title={"Delete Network Connection"}
          onConfirm={handleDelete}
          onClose={handleDeleteDialogClose}
          confirm="Confirm"
          cancel="Cancel"
          content={(
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon fontSize="large" sx={{ color: 'warning.main' }}/>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  Warning! Deleting network connection "{objToDel?.name}" is irreversible. The action you are about to take cannot be undone.
                </Typography>
              </Box>
              <MuiTextField
                id="confirm-connection-name"
                autoFocus
                fullWidth
                error={!nameConfirmed}
                helperText={!nameConfirmed && "Enter the name of your network connection to confirm delete."}
                size="medium"
                label="Type Network Connection Name"
                sx={{ mb: 2, mt: 4, }}
                onChange={handleNameInput}
                value={nameConfirm}
              />
            </Box>
          )}
        />}
      <NewNetworkConnection open={openCreate} setOpen={setOpenCreate} />
    </Box>
  )
}

export default NetworkConnectTable;
